import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { api } from '../api/client';
import {
  DOCUMENT_SORTS,
  type DocumentsSort,
  useCan,
  useContacts,
  useDocuments,
} from '../api/hooks';
import type { DocType, DocumentSummary } from '../api/types';
import { DocRow, DocTable } from '../components/DocRow';
import { ExportButtons } from '../components/ExportButtons';
import { InboxPanel } from '../components/InboxPanel';
import { LoadMore } from '../components/LoadMore';
import { Btn, Chip, Empty, Field, Search, SelectField } from '../components/ui';
import { VirtualRows } from '../components/VirtualRows';
import { useT } from '../i18n';
import { DOC_TYPES, isPayable, TYPES } from '../lib/doc';
import { czk, plural } from '../lib/format';
import { useCompanyFilter } from '../state/company';
import { useTopbar } from '../state/topbar';
import { useUi } from '../state/ui';
import { PaymentSheet } from './PaymentSheet';

/**
 * Typy v seznamu. Dobropisy a daňové doklady k platbě nemají vlastní chip – zobrazují se
 * vnořené pod fakturou / zálohou, ke které byly vystaveny (server je vrací jako `children`).
 */
const LIST_TYPES: DocType[] = ['FV', 'ZF', 'PF', 'DL'];

/** Chipy stavu. Nezaplacené a po splatnosti ukazuje přímo mince v řádku; `open`
 *  a `overdue` v URL dál fungují (odkazy z Přehledu), jen nemají vlastní chip. */
type StatusFilter = 'all' | 'open' | 'overdue' | 'paid' | 'draft' | 'cancelled';
const STATUSES: StatusFilter[] = ['all', 'open', 'overdue', 'paid', 'draft', 'cancelled'];
const STATUS_CHIPS: [StatusFilter, string][] = [
  ['all', 'Vše'],
  ['paid', 'Zaplacené'],
  ['draft', 'Koncepty'],
  ['cancelled', 'Stornované'],
];
const HIDDEN_STATUS_LABEL: Partial<Record<StatusFilter, string>> = {
  open: 'Nezaplacené',
  overdue: 'Po splatnosti',
};

/** Doklady i s vnořenými (dobropisy, daňové doklady) v pořadí, jak jsou v seznamu. */
const flatten = (docs: DocumentSummary[]) => docs.flatMap((d) => [d, ...(d.children ?? [])]);

/** CSV vybraných dokladů přímo z prohlížeče (středník, BOM pro Excel). */
function docsCsv(docs: DocumentSummary[]): string {
  const cell = (v: unknown) => {
    const s = String(v ?? '');
    return /[;"\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const rows = [
    [
      'Číslo',
      'Typ',
      'Odběratel',
      'Vystaveno',
      'DUZP',
      'Splatnost',
      'Měna',
      'Celkem',
      'Zaplaceno',
      'Stav',
      'VS',
    ],
    ...docs.map((d) => [
      d.number ?? 'koncept',
      d.type,
      d.customerName,
      d.issuedAt,
      d.taxDate,
      d.dueAt,
      d.currency ?? 'CZK',
      d.totalAmount.replace('.', ','),
      d.paidAmount.replace('.', ','),
      d.status,
      d.variableSymbol,
    ]),
  ];
  return `\uFEFF${rows.map((r) => r.map(cell).join(';')).join('\r\n')}\r\n`;
}

/**
 * Doklady: filtry (stav, typ, hledání, období, partner) jsou v URL, takže odkaz jde poslat;
 * výběr více dokladů umí hromadně označit odesláno a stáhnout CSV (kap. 37).
 */
export function Documents() {
  const t = useT();
  useTopbar(t('nav.documents'), { right: 'companyChip' });
  const { activeCompanyId } = useCompanyFilter();
  const { toast, openSheet } = useUi();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  /** Vydané (prodej) a přijaté (nákup) doklady jsou dvě oddělené evidence */
  const received = params.get('smer') === 'prijate';
  const access = useCan(activeCompanyId);
  const q = params.get('q') ?? '';
  const status = (
    STATUSES.includes(params.get('status') as StatusFilter) ? params.get('status') : 'all'
  ) as StatusFilter;
  const rawType = params.get('type') as DocType | null;
  // starý odkaz na ?type=DB / DD → ukáže faktury / zálohy, pod kterými jsou vnořené
  const type: 'all' | DocType =
    rawType === 'DB' ? 'FV' : rawType === 'DD' ? 'ZF' : (rawType ?? 'all');
  const from = params.get('from') ?? '';
  const to = params.get('to') ?? '';
  const contactId = params.get('contactId') ?? '';
  /** jen faktury, ke kterým chybí nákupní doklad (vykřičník v řádku) */
  const purchaseMissing = !received && params.get('nakup') === 'chybi';
  const rawSort = params.get('razeni') as DocumentsSort | null;
  const sort: DocumentsSort =
    rawSort && DOCUMENT_SORTS.includes(rawSort) ? rawSort : 'issuedAt:desc';
  const [more, setMore] = useState(!!(from || to || contactId || rawSort));
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState(false);
  const set = (patch: Record<string, string | undefined>) => {
    const next = new URLSearchParams(params);
    for (const [k, v] of Object.entries(patch)) {
      if (v) next.set(k, v);
      else next.delete(k);
    }
    setParams(next, { replace: true });
  };
  const {
    data: docs = [],
    isPending,
    total,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  } = useDocuments({
    companyId: activeCompanyId ?? undefined,
    direction: received ? 'received' : 'issued',
    type: type === 'all' || received ? undefined : type,
    status: status === 'all' ? undefined : status,
    q: q.trim() || undefined,
    from: from || undefined,
    to: to || undefined,
    contactId: contactId || undefined,
    group: '1',
    sort: sort === 'issuedAt:desc' ? undefined : sort,
    purchase: purchaseMissing ? 'missing' : undefined,
  });
  const all = flatten(docs);
  const { data: contacts = [] } = useContacts(more ? activeCompanyId : null);

  const sum = all
    .filter((d) => d.status !== 'cancelled' && d.status !== 'draft' && isPayable(d.type))
    .reduce((a, d) => a + (d.type === 'DB' ? -1 : 1) * Number(d.totalAmount), 0);
  const selecting = selected.size > 0;
  const toggle = (id: string) =>
    setSelected((cur) => {
      const next = new Set(cur);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const chosen = all.filter((d) => selected.has(d.id));

  const markSent = async () => {
    setBusy(true);
    let n = 0;
    for (const d of chosen) {
      if (d.sentAt || d.status === 'draft' || d.status === 'cancelled') continue;
      try {
        await api.post(`/api/documents/${d.id}/sent`);
        n += 1;
      } catch {
        /* server řekl ne – zbytek pokračuje */
      }
    }
    setBusy(false);
    setSelected(new Set());
    qc.invalidateQueries({ queryKey: ['documents'] });
    toast(`Označeno ${n} ${plural(n, ['doklad', 'doklady', 'dokladů'])}`);
  };

  const exportCsv = () => {
    const blob = new Blob([docsCsv(chosen)], {
      type: 'text/csv;charset=utf-8',
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `doklady-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  return (
    <>
      <div className="seg doc-tabs" role="tablist" aria-label="Evidence dokladů">
        <button
          type="button"
          role="tab"
          aria-selected={!received}
          className={received ? '' : 'on'}
          onClick={() => {
            setSelected(new Set());
            setParams(new URLSearchParams(), { replace: true });
          }}
        >
          Faktury vydané
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={received}
          className={received ? 'on' : ''}
          onClick={() => {
            setSelected(new Set());
            setParams(new URLSearchParams({ smer: 'prijate' }), { replace: true });
          }}
        >
          Faktury přijaté
        </button>
      </div>
      {received && activeCompanyId && (
        <InboxPanel
          companyId={activeCompanyId}
          canWrite={!access.loaded || access.can('documents:issue')}
        />
      )}
      <Search
        value={q}
        onChange={(v) => set({ q: v || undefined })}
        placeholder={received ? 'Číslo, dodavatel nebo VS' : 'Číslo, odběratel nebo VS'}
      />
      <div className="chips">
        {STATUS_CHIPS.map(([k, l]) => (
          <Chip
            key={k}
            on={status === k}
            onClick={() => set({ status: k === 'all' ? undefined : k })}
          >
            {l}
          </Chip>
        ))}
        {HIDDEN_STATUS_LABEL[status] && (
          <Chip on onClick={() => set({ status: undefined })}>
            {HIDDEN_STATUS_LABEL[status]} ✕
          </Chip>
        )}
        {!received && (
          <Chip
            on={purchaseMissing}
            onClick={() => set({ nakup: purchaseMissing ? undefined : 'chybi' })}
          >
            ! Chybí nákupní doklad
          </Chip>
        )}
        <Chip on={more} onClick={() => setMore((v) => !v)}>
          Období, partner a řazení{from || to || contactId || rawSort ? ' •' : ''}
        </Chip>
      </div>
      {!received && (
        <div className="chips">
          <Chip on={type === 'all'} onClick={() => set({ type: undefined })}>
            Všechny typy
          </Chip>
          {DOC_TYPES.filter(
            (t) => LIST_TYPES.includes(t) || type === t || docs.some((d) => d.type === t),
          ).map((t) => (
            <Chip key={t} on={type === t} onClick={() => set({ type: t })}>
              {TYPES[t].plural}
            </Chip>
          ))}
        </div>
      )}
      {more && (
        <div className="filters">
          <Field
            id="df-from"
            label="Od"
            type="date"
            value={from}
            onChange={(e) => set({ from: e.target.value || undefined })}
          />
          <Field
            id="df-to"
            label="Do"
            type="date"
            value={to}
            onChange={(e) => set({ to: e.target.value || undefined })}
          />
          <SelectField
            id="df-contact"
            label="Partner"
            value={contactId}
            onChange={(e) => set({ contactId: e.target.value || undefined })}
          >
            <option value="">Všichni</option>
            {contacts.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </SelectField>
          <SelectField
            id="df-sort"
            label="Řazení"
            value={sort}
            onChange={(e) =>
              set({ razeni: e.target.value === 'issuedAt:desc' ? undefined : e.target.value })
            }
          >
            <option value="issuedAt:desc">Od nejnovějšího data</option>
            <option value="issuedAt:asc">Od nejstaršího data</option>
            <option value="number:desc">Podle čísla, od nejvyššího</option>
            <option value="number:asc">Podle čísla, od nejnižšího</option>
          </SelectField>
        </div>
      )}
      <div className="listmeta">
        <span>
          {total ?? docs.length} {plural(total ?? docs.length, ['doklad', 'doklady', 'dokladů'])}
          {selecting ? ` · vybráno ${selected.size}` : ''}
        </span>
        <span>{czk(sum)}</span>
      </div>
      <div className="actions">
        {received && (
          <Btn sm variant="primary" onClick={() => navigate('/doklady/prijata/nova')}>
            Nahrát přijatou fakturu
          </Btn>
        )}
        <Btn sm onClick={() => setSelected(selecting ? new Set() : new Set(all.map((d) => d.id)))}>
          {selecting ? t('documents.cancelSelection') : t('documents.selectAll')}
        </Btn>
        {selecting && (
          <>
            <Btn sm loading={busy} onClick={markSent}>
              {t('documents.markSent')}
            </Btn>
            <Btn sm onClick={exportCsv}>
              CSV výběru
            </Btn>
          </>
        )}
        <ExportButtons
          kind="documents"
          companyId={activeCompanyId}
          from={from || undefined}
          to={to || undefined}
        />
      </div>
      <DocTable selectable partyLabel={received ? 'Dodavatel' : 'Odběratel'}>
        {docs.length ? (
          <VirtualRows
            items={docs}
            render={(d) => (
              <div key={d.id} className="doc-group">
                {[d, ...(d.children ?? [])].map((x) => (
                  <div key={x.id} className={`sel-row ${selected.has(x.id) ? 'on' : ''}`.trim()}>
                    <label className="sel-hit">
                      <input
                        type="checkbox"
                        aria-label={`Vybrat ${x.number ?? 'koncept'}`}
                        checked={selected.has(x.id)}
                        onChange={() => toggle(x.id)}
                      />
                    </label>
                    <DocRow
                      doc={x}
                      child={x !== d}
                      onPay={(doc) =>
                        openSheet(<PaymentSheet doc={doc} onDone={() => undefined} />)
                      }
                    />
                  </div>
                ))}
              </div>
            )}
          />
        ) : (
          <Empty>
            {isPending
              ? 'Načítám…'
              : received && !q && !from && !to && !contactId && status === 'all'
                ? 'Zatím žádné přijaté faktury – nahraj první tlačítkem výše.'
                : 'Žádný doklad neodpovídá filtru.'}
          </Empty>
        )}
      </DocTable>
      <LoadMore hasMore={hasNextPage} loading={isFetchingNextPage} onMore={fetchNextPage} />
    </>
  );
}
